import { Box, Tooltip, useTheme } from '@mui/material';
import { useEngineStrings } from '../../user_guides/i18n/useEngineStrings';
import { interpolate } from '../../utils/entryType';
import { HORIZON_MONTHLY, HORIZON_PLANNING } from '../../utils/budgetsByCategory';

/**
 * Horizon chip (#2175, #2179, DESIGN_SYSTEM.md 8.22): one segment reading a
 * Normal budget's current horizon, `M2M` or `Plan` on rows and the full
 * word on the budget detail hero (`wide`). Month to month wears the
 * Segmented control's active look: `--surface-hover` fill, a 1px inset
 * `--interactive` ring and `--text-primary` text. Planning ahead keeps the
 * same fill and white text and swaps the ring for the positive color, so
 * the two horizons tell apart by their border alone (owner, #2179).
 *
 * With `onPress` the chip is a button: hover deepens the ring, Enter and
 * Space activate it, and both the click and the key stop at the chip so a
 * row's own navigation never fires. Without `onPress` (viewers) it is a
 * plain span with no tab stop.
 *
 * Both forms carry the two explanation sentences as a hover note that opens
 * after a 2 second delay (a long press on touch).
 *
 * @param {object} props
 * @param {string} props.horizon       `monthly` or `planningAhead`
 * @param {boolean} [props.wide]       the full word instead of M2M or Plan
 * @param {function} [props.onPress]   opens the switch dialog; absent = display only
 */
const HorizonChip = ({ horizon, wide = false, onPress }) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const strings = useEngineStrings();
  const current = horizon === HORIZON_PLANNING ? HORIZON_PLANNING : HORIZON_MONTHLY;
  const interactive = typeof onPress === 'function';
  const fullName = strings[current === HORIZON_PLANNING ? 'budgets.horizonPlanningAhead' : 'budgets.horizonMonthly'];
  const label = wide ? fullName : strings[current === HORIZON_PLANNING ? 'budgets.horizonChipPlanning' : 'budgets.horizonChipMonthly'];
  const note = `${strings['budgets.horizonExplainMonthly']} ${strings['budgets.horizonExplainPlanning']}`;
  const ariaLabel = interpolate(strings[interactive ? 'budgets.horizonChipAriaChange' : 'budgets.horizonChipAria'], { horizon: fullName });

  const planning = current === HORIZON_PLANNING;
  const ring = planning ? palette.success.main : palette.interactive;
  const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    margin: 0,
    padding: wide ? '2px 10px' : '1px 6px',
    border: 0,
    borderRadius: '4px',
    backgroundColor: palette.surface.hover,
    boxShadow: `inset 0 0 0 1px ${ring}`,
    color: palette.text.primary,
    font: 'inherit',
    fontSize: wide ? '11px' : '10px',
    lineHeight: wide ? '14px' : '12px',
    fontWeight: ds.font.weightMedium,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: `box-shadow ${ds.motion.fast} ${ds.motion.easeOut}`,
    ...(interactive ? {
      cursor: 'pointer',
      '&:hover': { boxShadow: `inset 0 0 0 1px ${planning ? palette.positiveStrong : palette.interactiveStrong}` },
      '&:focus-visible': { outline: `1.5px solid ${ring}`, outlineOffset: '2px' },
    } : {}),
  };
  const shared = { 'data-testid': 'horizon-chip', 'data-horizon': current, 'data-wide': wide || undefined, 'aria-label': ariaLabel, sx: chipSx };

  const chip = interactive ? (
    <Box
      component="button"
      type="button"
      onClick={(e) => { e.stopPropagation(); onPress(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
      {...shared}
    >
      {label}
    </Box>
  ) : (
    <Box component="span" role="img" {...shared}>{label}</Box>
  );

  return (
    <Tooltip title={note} placement="top" describeChild enterDelay={2000} enterNextDelay={2000} enterTouchDelay={700}>
      <Box component="span" sx={{ display: 'inline-flex' }}>{chip}</Box>
    </Tooltip>
  );
};

export default HorizonChip;
